import { Fragment, ChangeEvent, SyntheticEvent, useEffect } from 'react'
import { useTranslation, Trans } from 'next-i18next'
import { useRouter } from 'next/router'

import OrLine from '~/components/OrLine'
import ROUTES from '~/shared/constants/routes.const'
import { ButtonPrimary } from '~/components/Buttons/ButtonPrimary'
import { FieldError } from '~/components/FieldError'
import { InputPhoneNumber } from '~/components/InputPhoneNumber'
import { InputV2 } from '~/components/InputV2'
import { SelectBox } from '~/components/SelectBox'
import { SignUpScenario } from '~/types/sign-up'
import { UserField } from '~/types/user'
import { countryData } from '~/shared/constants/locale.const'
import { selectCaptcha, selectAuthType } from '~/auth/slices/recaptcha.slice'
import { isValidPhoneNumberInput } from '~/utils/helpers'
import { isAuthenticated } from '~/auth/slices/auth.slice'
import { useAppSelector } from '~/redux/hooks'
import useCurrentUser from '~/hooks/useCurrentUser'
import ButtonGoogleLogin from '~/auth/components/ButtonGoogleLogin'
import registerAnalytics from '~/analytics/register/registerAnalytics'
import homApi from '~/services/hom-api.service'
import { Link } from '~/components/Link'
import useRegisterContext from '~/auth/hooks/useRegisterContext'
import { useRegister } from '~/auth/hooks/useRegister'
import { isCheckoutScenario, prependCountryCallingCodeToInput } from '~/auth/utils/register.utils'
import { AuthTypes } from '~/auth/types/auth.types'
import ButtonFacebookLogin from '~/auth/components/ButtonFacebookLogin'
import RegisterServerError from '~/auth/components/RegisterServerError'
import CaptchaChallenge from '~/auth/components/CaptchaChallenge'
import { selectIsSubscriptionApplicable } from '~/redux/slices/subscription.slice'

export function InitialPage({
  hidePasswordFields,
  showMathLevelSelector = true,
  showYearsInSchoolSelector = true,
  showRegisterForm,
}: {
  hidePasswordFields?: boolean
  showMathLevelSelector?: boolean
  showYearsInSchoolSelector?: boolean
  showRegisterForm?: boolean
}): React.ReactElement {
  const { t } = useTranslation(['onboarding', 'registerAccount', 'fields', 'validation', 'subscription'])
  const router = useRouter()

  const { fields, errors, setFields, setErrors, signUpScenario } = useRegisterContext()

  const captcha = useAppSelector(selectCaptcha)
  const authType = useAppSelector(selectAuthType)
  const { register, loading } = useRegister()

  const authenticated = useAppSelector(isAuthenticated)
  const user = useCurrentUser()
  const emailDisabled = Boolean(user?.facebookEmail || user?.email || user?.authEmail)
  const isCheckout = isCheckoutScenario(signUpScenario)
  const showCaptchaChallenge = captcha.challenge !== '' && authType === AuthTypes.EMAIL
  const isRegistration = router.pathname.startsWith(ROUTES.REGISTER)
  const isDefaultSignUpScenario = signUpScenario === SignUpScenario.DEFAULT
  const isSubscriptionApplicable = useAppSelector(selectIsSubscriptionApplicable)

  useEffect(() => {
    if (fields.email) {
      return
    }

    const defaultEmail = fields.facebookEmail || fields.authEmail

    if (defaultEmail) {
      setFields((prev) => ({ ...prev, email: defaultEmail }))
    }
  }, [fields, setFields])

  const handleOnChange = (event: ChangeEvent<HTMLSelectElement>): void => {
    const { name, value } = event.target
    registerAnalytics.changeInSelectOption(name)

    const fieldValue = name === UserField.SKILL || name === UserField.YEARS_IN_SCHOOL ? Number(value) : value

    setFields((prevState) => ({ ...prevState, [name]: fieldValue }))
  }

  const handleOnCountryCodeChange = (code: string): void => {
    const selectedCountry = countryData.find((country) => {
      return country.code === code
    })

    if (selectedCountry) {
      setFields((prevState) => ({
        ...prevState,
        countryCallingCode: selectedCountry.callingCode,
        countryCode: selectedCountry.code,
      }))
    }

    registerAnalytics.changeInSelectOption('phone-country-code')
  }

  const handleOnPhoneNumberChange = (phoneNumber: string): void => {
    const numberWithoutCallerCode = phoneNumber.slice(fields.countryCallingCode.length + 1)

    if (isValidPhoneNumberInput(numberWithoutCallerCode)) {
      setFields((prevState) => ({
        ...prevState,
        phone: numberWithoutCallerCode,
      }))

      if (numberWithoutCallerCode.length === 1) {
        registerAnalytics.writingInForms('phoneNumber')
      }
    }
  }

  const handleFormSubmit = async (e: SyntheticEvent): Promise<void> => {
    e.preventDefault()

    if (fields.email && !isRegistration) {
      const { exists: emailAlreadyInUse } = await homApi.getUserWithEmailExists(fields.email)

      if (emailAlreadyInUse) {
        setErrors((errorsState) => ({ ...errorsState, email: t('onboarding:errors.emailTaken') }))
        registerAnalytics.logButtonClick('register:error-non-unique-email')
        return
      }
    }

    registerAnalytics.logButtonClick('register')
    register()
  }

  const handleOnChangeTextFields = (name: string, value: string): void => {
    // track only when the first character is placed

    if (value.length === 1) {
      registerAnalytics.writingInForms(name)
    }
    setFields((prev) => ({ ...prev, [name]: value }))
  }

  return showRegisterForm ? (
    <div className="w-full lg:w-125">
      {emailDisabled && !isDefaultSignUpScenario ? (
        <h1 className="text-h4 lg:text-8 font-bold text-center mb-2 text-white">{t('registerAccount:studentInfo')}</h1>
      ) : (
        <h1 className="pt-2 lg:pt-0 text-6 lg:text-8 font-bold text-center mb-2">
          {Trans({
            t,
            i18nKey: 'registerAccount:freeSimpleGamified',
            components: { green: <span className="bg-green-emerald text-white px-2" />, br: <br /> },
            children: t('registerAccount:freeSimpleGamified'),
          })}
        </h1>
      )}

      {(!authenticated || isDefaultSignUpScenario) && (
        <>
          <div className="flex flex-col items-center justify-center gap-3 w-full">
            <p>{t('registerAccount:signUpWith')}</p>
            <div className="flex gap-3 w-full">
              <ButtonGoogleLogin redirectBack />
              <ButtonFacebookLogin redirectBack />
            </div>
          </div>
          <OrLine className="my-4" lineColor="bg-black" text={t('registerAccount:orContinue')} textColor="text-black" />
        </>
      )}

      <form className="flex flex-col mt-2" onSubmit={handleFormSubmit}>
        {isCheckout && (
          <Fragment>
            <InputV2
              autoComplete="given-name"
              fieldErrors={errors.fullName}
              label={t('fields:fullName')}
              name="fullName"
              setValue={(val) => handleOnChangeTextFields('fullName', val)}
              type="text"
              value={fields.fullName || ''}
            />

            <InputPhoneNumber
              autoComplete="off"
              countryCode={fields.countryCode}
              fieldErrors={errors.phone}
              label={t('fields:phone')}
              name="phoneNumber"
              setCountryCode={(newCode) => handleOnCountryCodeChange(newCode)}
              setValue={(val) => handleOnPhoneNumberChange(val)}
              type="text"
              value={prependCountryCallingCodeToInput(fields.countryCallingCode, fields.phone)}
            />
          </Fragment>
        )}

        <InputV2
          autoComplete="email"
          fieldErrors={errors.email}
          label={t('fields:email')}
          name="email"
          setValue={(val) => handleOnChangeTextFields('email', val)}
          type="text"
          value={fields.email || ''}
        />

        {(!hidePasswordFields || isDefaultSignUpScenario) && (
          <Fragment>
            <InputV2
              autoComplete="off"
              fieldErrors={errors.password}
              label={t('fields:password')}
              name="password"
              setValue={(val) => handleOnChangeTextFields('password', val)}
              type="password"
              value={fields.password || ''}
            />
            <InputV2
              autoComplete="off"
              fieldErrors={errors.passwordConfirm}
              label={t('fields:passwordConfirm')}
              name="passwordConfirm"
              setValue={(val) => handleOnChangeTextFields('passwordConfirm', val)}
              type="password"
              value={fields.passwordConfirm || ''}
            />
          </Fragment>
        )}

        {!isRegistration && (
          <div className="flex flex-col md:flex-row justify-between">
            <div className="flex flex-col sm:flex-1 ml-0 md:mr-2 my-2 sm:my-0 mt-0">
              <SelectBox name={UserField.COUNTRY} onChange={handleOnChange} value={fields.country || ''} />
              <FieldError error={errors.country} />
            </div>
            <div className="flex flex-col sm:flex-1 md:ml-2 mt-1 md:mt-0">
              <SelectBox name={UserField.BIRTH_YEAR} onChange={handleOnChange} value={fields.birthYear || ''} />
              <FieldError error={errors.birthYear} />
            </div>
          </div>
        )}

        {isCheckout && showMathLevelSelector && showYearsInSchoolSelector && (
          <div className="flex justify-between sm:mt-4 flex-col sm:flex-row w-86 lg:w-125">
            <div className="flex flex-col sm:flex-1 ml-0 sm:mr-2 my-2 sm:my-0 w-full">
              <SelectBox
                name={UserField.SKILL}
                onChange={handleOnChange}
                value={typeof fields.skill === 'number' ? String(fields.skill) : ''}
              />
              <FieldError error={errors.skill} />
            </div>

            <div className="flex flex-col sm:flex-1 mr-0 sm:ml-2 my-2 sm:my-0 w-full">
              <SelectBox
                name={UserField.YEARS_IN_SCHOOL}
                onChange={handleOnChange}
                value={typeof fields.yearsInSchool === 'number' ? String(fields.yearsInSchool) : ''}
              />
              <FieldError error={errors.yearsInSchool} />
            </div>
          </div>
        )}

        <RegisterServerError />

        {showCaptchaChallenge && (
          <div className="mt-2 sm:mt-4">
            <CaptchaChallenge hideSubmitBtn loading={loading} />
          </div>
        )}
        <div className="flex flex-col justify-center items-center space-y-4 mt-3">
          <ButtonPrimary
            className={`font-semibold text-md py-3.5 ${
              isRegistration ? '!bg-orange !text-black shadow-task-light-orange' : ''
            }`}
            disabled={loading}
            disableRing
            isFullWidth
            isLoading={loading}
            noPadding
            size="medium"
            testId="continue-btn"
            type="submit"
          >
            {isRegistration
              ? isSubscriptionApplicable
                ? t('registerAccount:startFreeTrial')
                : t('registerAccount:getFreeAccount')
              : t('registerAccount:continue')}
          </ButtonPrimary>
        </div>
        {isRegistration && (
          <div className="flex justify-center gap-2 mt-4 text-black font-semibold">
            <p>{t('registerAccount:alreadyHaveAccount')}</p>
            <Link className="text-purple-electric hover:underline" to={{ pathname: ROUTES.LOGIN, query: router.query }}>
              {t('registerAccount:logIn')}
            </Link>
          </div>
        )}
      </form>
    </div>
  ) : (
    <></>
  )
}
